'use client'

import { useState } from 'react'
import Link from 'next/link'

import seoUrlPathEncode from '@lib/helpers/client/seo-url-path-encode'

import styles from '../jobs.module.scss'

type JobsCategoriesSectionProps = {
  locale: string
  categories: Record<string, Array<{ label: string; keyword: string; alt: string }>>
}

export default function JobsCategoriesSection({ locale, categories }: JobsCategoriesSectionProps): JSX.Element {
  const [showAll, setShowAll] = useState(false)

  // Extract category names (keys) from the categories object
  const categoryNames = categories && Object.keys(categories).length > 0 ? Object.keys(categories).sort() : []

  // Show first 10 categories initially (2 full rows on large screens with 5 columns)
  // This ensures the last visible row is full
  const initialCount = 10
  const visibleCategories = showAll ? categoryNames : categoryNames.slice(0, initialCount)
  const hasMore = categoryNames.length > initialCount

  return (
    <section className={styles.jobsCategoriesSection}>
      <div className={styles.jobsCategoriesContainer}>
        <div className={styles.jobsCategoriesCard}>
          <h2 className={styles.jobsCategoriesTitle}>By category</h2>
          <div className={styles.jobsCategoriesList}>
            {visibleCategories.map((category) => {
              // Determine URL based on whether category has subcategories
              const subCategories = categories[category]
              const url =
                subCategories && subCategories.length > 0
                  ? `/${locale}/jobs/browse/${seoUrlPathEncode(category, 'role')}`
                  : `/${locale}/jobs?query=${encodeURIComponent(category)}&location_name=All Locations&macro=${encodeURIComponent(category)}`

              return (
                <Link key={category} href={url} className={styles.jobsCategoryLink}>
                  {category}
                </Link>
              )
            })}
          </div>
          {hasMore && (
            <button onClick={() => setShowAll(!showAll)} className={styles.jobsCategoriesToggle}>
              {showAll ? 'Show less' : `Show all ${categoryNames.length} categories`}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`${styles.jobsCategoriesToggleIcon} ${showAll ? styles.jobsCategoriesToggleIconRotated : ''}`}
              >
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
